import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, TextField, Typography, Paper, FormHelperText, IconButton, List, ListItem, ListItemSecondaryAction, Autocomplete, Grid } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Spinner from "../../../Component/spinner/spinner";
import {
  useGetResourceByIdQuery,
  useSaveResourceMutation,
  useUpdateResourceMutation
} from "../../../app/services/resourceHubServices";
import { useGetAllResourceCategoriesQuery } from "../../../app/services/resourceCategoryServices";
import { Editor } from "primereact/editor";
import { selectCurrentScope, selectChapterId, selectSigId, selectUserId } from "../../../app/auth/authSlice";
import { useSelector } from "react-redux";

export default function AddEditResource() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const chapterId = useSelector(selectChapterId);
  const sigId = useSelector(selectSigId);
  const userId = useSelector(selectUserId)
  const memberScope = useSelector(selectCurrentScope);
  const currentLocation = useLocation();
  var currentPath = currentLocation.pathname.split('/');

  const [existingFiles, setExistingFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const { data: categoryData } = useGetAllResourceCategoriesQuery();
  const categories = categoryData?.data || [];

  const { data: resourceData, isLoading: isFetching } = useGetResourceByIdQuery(id, { skip: !isEditMode });
  const [saveResource, { isLoading: isSaving }] = useSaveResourceMutation();
  const [updateResource, { isLoading: isUpdating }] = useUpdateResourceMutation();

  const loading = isFetching || isSaving || isUpdating;

  const { control, handleSubmit, setValue, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      youtubeLink: "",
      resourceCategoryId: "",
    },
  });

  useEffect(() => {
    if (isEditMode && resourceData?.data) {
      const data = resourceData.data;
      setValue("name", data.name);
      setValue("description", data.description || "");
      setValue("youtubeLink", data.youtubeLink || "");
      setValue("resourceCategoryId", data.resourceCategoryId?._id || data.resourceCategoryId);
      if (data.files) setExistingFiles(data.files);
    }
  }, [isEditMode, resourceData, setValue]);

  const handleFileChange = (e) => {
    const rawFiles = Array.from(e.target.files);
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    
    let validFiles = [];
    let hasInvalidFormat = false;
    let hasSizeError = false;
    const maxSize = 5 * 1024 * 1024; // 5MB

    rawFiles.forEach((file) => {
      const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      const isFormatValid = allowedExtensions.includes(fileExt) || file.type.startsWith('image/') || file.type === 'application/pdf' || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      
      if (!isFormatValid) {
        hasInvalidFormat = true;
        return;
      }

      if (file.size > maxSize) {
        hasSizeError = true;
        return;
      }

      validFiles.push(file);
    });

    if (hasInvalidFormat) {
      toast.warning("Some files were omitted. Only .pdf, .doc, .docx, and image files are allowed.");
    }
    if (hasSizeError) {
      toast.warning("Some files were omitted. Each file size cannot exceed 5MB.");
    }

    if (validFiles.length === 0) return;

    const currentTotal = existingFiles.length - filesToDelete.length + selectedFiles.length;
    let availableSlots = 5 - currentTotal;
    
    if (availableSlots <= 0) {
      toast.error("You can upload a maximum of 5 files.");
      return;
    }

    if (validFiles.length > availableSlots) {
      toast.warning(`Only ${availableSlots} more file(s) can be added. Extra files omitted.`);
      validFiles = validFiles.slice(0, availableSlots);
    }

    const newFilesWithNames = validFiles.map((file) => {
      const defaultName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      return { file, customName: defaultName, fileName: defaultName };
    });
    setSelectedFiles([...selectedFiles, ...newFilesWithNames]);
  };

  const removeSelectedFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  const markFileForDeletion = (fileId) => {
    setFilesToDelete([...filesToDelete, fileId]);
    setExistingFiles(existingFiles.filter(f => f.fileId !== fileId));
  };

  const onSubmit = async (formData) => {
    try {
      const data = new FormData();
      data.append("name", formData.name);
      data.append("description", formData.description);
      data.append("youtubeLink", formData.youtubeLink);
      data.append("resourceCategoryId", formData.resourceCategoryId);

      data.append("createdBy[id]", userId);
      data.append("createdBy[role]", memberScope);

      if (memberScope === 'PORTAL_CHAPTER_ADMIN' && chapterId) {
        data.append("chapterId", chapterId);
      } else if (memberScope === 'PORTAL_SIG_ADMIN' && sigId) {
        data.append("SIGId", sigId);
      }
      const updatedFiles = [
        ...existingFiles
          .filter(f => !filesToDelete.includes(f.fileId))
          .map(f => f.fileUrl.split("/web/")[1]),
        ...selectedFiles.map(f => f.file)
      ];
      updatedFiles.forEach((fileObj) => {
        data.append("files", fileObj);
      });
      const updatedFileNames = [
        ...existingFiles
          .filter(f => !filesToDelete.includes(f.fileId))
          .map(f => f.fileName),
        ...selectedFiles.map(f => f.fileName)
      ];
      data.append("fileNames", JSON.stringify(updatedFileNames));

      if (isEditMode) {
        if (filesToDelete.length > 0) {
          data.append("filesToDelete", JSON.stringify(filesToDelete));
        }
        // data.append("existingFiles", JSON.stringify(existingFiles));
      }

      let resData;
      if (isEditMode) {
        resData = await updateResource({ id, data }).unwrap();
      } else {
        resData = await saveResource(data).unwrap();
      }

      if (resData.status) {
        toast.success(resData.message || `Resource ${isEditMode ? "updated" : "created"} successfully!`);
        navigate(`/${currentPath[1]}/resource-hub`);
      } else {
        toast.error(resData.err?.message || "Operation failed");
      }
    } catch (error) {
      toast.error("Error submitting form" + JSON.stringify(error));
    }
  };

  return (
    <Box >
      <div className="pt-5">
        <div className="chapter-heading">
          <h2 className="text-center title-font text-light py-3 mt-1">{isEditMode ? "Update Resource Category" : "Add Resource Category"}</h2>
        </div>
      </div>
      <div className='p-md-5 p-3 overallPadding'>
        <div className="text-end w-100">
          <Button variant="contained" className="menus-color poppins-regular" style={{ color: "white", marginBottom: "15px" }} onClick={() => window.history.back()}>
            Back
          </Button>
        </div>
        <Paper sx={{ boxShadow: "none" }}>
          {isFetching && isEditMode ? (
            <Box display="flex" justifyContent="center"><Spinner /></Box>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box mb={2}>
                    <Controller
                      control={control}
                      name={'resourceCategoryId'}
                      rules={{ required: 'Please select a resource category' }}
                      render={({ field, fieldState }) => (
                        <>
                          <Autocomplete
                            id="resource-category"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            options={categories}
                            getOptionLabel={(option) =>
                              option?.name || ""
                            }
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            filterSelectedOptions
                            onChange={(event, value) => {
                              // Check if value is not null before accessing _id
                              field.onChange(value ? value._id : "");
                            }}
                            value={
                              field.value
                                ? categories.find((group) => group._id === field.value) || null
                                : null
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="resourceCategoryId"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                label={
                                  <Typography className="text-dark">
                                    Resource Category{" "}
                                    <span style={{ color: "red" }}>*</span>
                                  </Typography>
                                }
                                placeholder="Select Resource Category"
                              />
                            )}
                          />
                        </>
                      )}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box mb={2}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: "Resource Name is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={
                            <Typography className="text-dark">
                              Resource Name <span style={{ color: "red" }}>*</span>
                            </Typography>
                          }
                          fullWidth error={!!errors.name} helperText={errors.name?.message} />
                      )}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mb={2}>
                    <Controller
                      name="description"
                      control={control}
                      rules={{
                        validate: (v) => {
                          const cleanText = v
                            ? v
                              .replace(/<[^>]*>/g, " ")
                              .replace(/&nbsp;/g, " ")
                              .replace(/\s+/g, " ")
                              .trim()
                            : "";
                          if (!cleanText) {
                            return "Description is required";
                          }
                          const wordCount = cleanText.split(/\s+/).filter(Boolean).length;
                          if (wordCount > 250) {
                            return `Description cannot exceed 250 words. Current word count: ${wordCount}`;
                          }
                          return true;
                        }
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <div style={{ width: "100%" }}>
                          <Typography className="text-dark" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                            Description <span style={{ color: "red" }}>*</span>
                          </Typography>
                          <Editor
                            value={field.value || ""}
                            onTextChange={(e) => field.onChange(e.htmlValue || "")}
                            style={{ height: "200px" }}
                            placeholder="Enter description"
                            headerTemplate={<span className="ql-formats">
                              <select className="ql-header" aria-label="Header" />
                              <button className="ql-bold" aria-label="Bold" />
                              <button className="ql-italic" aria-label="Italic" />
                              <button className="ql-underline" aria-label="Underline" />
                              <button className="ql-strike" aria-label="Strike" />
                              <select className="ql-align" value="" aria-label="Align" />
                              <button className="ql-list" value="ordered" aria-label="Ordered List" />
                              <button className="ql-list" value="bullet" aria-label="Unordered List" />
                              <select className="ql-color" />
                              <select className="ql-background" />
                              <button className="ql-clean" aria-label="Clear Formatting" />
                            </span>}
                          />
                          {error && (
                            <FormHelperText error>{error.message}</FormHelperText>
                          )}
                        </div>
                      )}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box mb={2}>
                    <Controller
                      name="youtubeLink"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="YouTube Link" fullWidth />
                      )}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box mb={2}>
                    <div className='d-flex align-items-center gap-2 justify-content-between'>
                      <Typography variant="h6">Attachments <span className="text-danger">*</span></Typography>
                      <Button variant="outlined" component="label">
                        Upload Files
                        <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.avif,.svg" hidden multiple onChange={handleFileChange} />
                      </Button>
                    </div>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Allowed formats: .pdf, .doc, .docx, images. Maximum 5 files allowed. Each file size cannot exceed 5 MB.
                    </Typography>
                    <List>
                      {existingFiles.map((file, index) => (
                        <ListItem
                          key={file.fileId}
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: 1,
                            borderBottom: '1px solid #eee',
                            pb: 2,
                            pr: 8
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                              Existing File: {file.originalFileName}
                            </Typography>
                            <ListItemSecondaryAction>
                              <IconButton edge="end" aria-label="delete" onClick={() => markFileForDeletion(file.fileId)}>
                                <DeleteIcon color="error" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </Box>
                          <TextField
                            size="small"
                            label="Display Name"
                            value={file.fileName ?? file.originalFileName ?? ''}
                            onChange={(e) => {
                              const updated = [...existingFiles];
                              updated[index] = { ...file, fileName: e.target.value };
                              setExistingFiles(updated);
                            }}
                            fullWidth
                          />
                        </ListItem>
                      ))}
                      {selectedFiles.map((fileObj, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: 1,
                            borderBottom: '1px solid #eee',
                            pb: 2,
                            pr: 8
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                              New File: {fileObj.file.name}
                            </Typography>
                            <ListItemSecondaryAction>
                              <IconButton edge="end" aria-label="delete" onClick={() => removeSelectedFile(index)}>
                                <DeleteIcon color="error" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </Box>
                          <TextField
                            size="small"
                            label="Display Name"
                            value={fileObj.fileName || ''}
                            onChange={(e) => {
                              const updated = [...selectedFiles];
                              updated[index] = { ...fileObj, fileName: e.target.value };
                              setSelectedFiles(updated);
                            }}
                            fullWidth
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button variant="outlined" onClick={() => navigate(`/${currentPath[1]}/resource-hub`)} disabled={loading}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary" disabled={loading}>
                      {isEditMode ? "Update" : "Save"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}
        </Paper>
      </div>
    </Box>
  );
}
